import React, { useRef, useEffect, useState } from 'react';
import { Box, Card, ToggleButton, ToggleButtonGroup } from '@mui/material';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import * as turf from '@turf/turf';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapOutlined as MapIcon } from '@mui/icons-material';
import PageHeader from '../../components/ui/PageHeader';
import PageBody from '../../components/ui/PageBody';
import { EmptyState } from '../../components/ui/States';
import DashboardPanel from '../dashboard/DashboardPanel';

// The service-area map, parked.
//
// This was the whole of the Dashboard — the first screen after login showed
// a map and no numbers, while /files and /operations were already computing
// the compliance score, the attention count and the running-workflow count
// that an AO actually opens the product to see. The map is not useful
// enough to be the landing screen, but it is built and it works, so it
// keeps a route (/coverage, linked from the Dashboard panel) rather than
// being deleted. It is not in the rail.
//
// Its stylesheet is gone. Dashboard.css set `* { font-family: 'Roboto' }`
// and restyled `html, body`, which — because CRA bundles page CSS globally
// — decided the font for every screen in the app. Everything it did that
// was worth keeping is inline here or in the theme.

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';

type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

interface PolygonData {
  coordinates: number[][];
}

interface PolygonGroup {
  workAreaNumber: string;
  polygons: PolygonData[];
}

const getTodayDayName = (): DayOfWeek => {
  const days: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[new Date().getDay()];
};

const CoverageMap: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const searchPopup = useRef<mapboxgl.Popup | null>(null);
  const [selectedDay, setSelectedDay] = useState<DayOfWeek>(getTodayDayName());
  // `new mapboxgl.Map()` throws outright when there is no WebGL context and
  // when the token is missing. Unguarded, that took the whole app down.
  const [mapError, setMapError] = useState<string | null>(
    mapboxgl.accessToken ? null : 'No Mapbox token is configured for this environment.',
  );

  // Initialize map
  useEffect(() => {
    if (map.current || !mapContainer.current || !mapboxgl.accessToken) return;

    const container = mapContainer.current;
    let resizeObserver: ResizeObserver | undefined;

    try {
      const geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl as any,
        marker: false,
        placeholder: 'Search for an address',
        zoom: 14,
      });

      map.current = new mapboxgl.Map({
        container,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-77.12, 38.77],
        zoom: 11.5,
      });

      map.current.addControl(geocoder, 'top-left');

      // Tell the user which work area an address falls in.
      geocoder.on('result', (e: any) => {
        searchPopup.current?.remove();

        const point = turf.point([e.result.center[0], e.result.center[1]]);
        let matchedWorkArea: string | null = null;

        map.current?.getStyle().layers.forEach((layer) => {
          if (
            !layer.id.startsWith('polygon-') ||
            layer.id.includes('-border') ||
            layer.id.includes('-label')
          ) return;

          const source = map.current?.getSource(layer.id) as mapboxgl.GeoJSONSource;
          if (!source) return;

          const sourceData = (source as any)._data;
          if (!sourceData) return;

          if (turf.booleanPointInPolygon(point, turf.polygon(sourceData.geometry.coordinates))) {
            matchedWorkArea = sourceData.properties.label;
          }
        });

        if (matchedWorkArea && map.current) {
          searchPopup.current = new mapboxgl.Popup()
            .setLngLat(e.result.center)
            .setHTML(
              `<div style="font-size:15px;font-weight:700;padding:10px 12px;">${matchedWorkArea}</div>`
            )
            .addTo(map.current);
        }
      });

      resizeObserver = new ResizeObserver(() => map.current?.resize());
      resizeObserver.observe(container);
    } catch (err) {
      // No WebGL, a bad token, a blocked tile host — all of these throw from
      // the constructor. Report it in the page instead of unmounting the app.
      setMapError(
        err instanceof Error && /webgl/i.test(err.message)
          ? 'This browser could not start WebGL, which the map needs.'
          : 'The map could not be loaded.'
      );
    }

    return () => {
      resizeObserver?.disconnect();
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // Fetch and render polygons based on selected day
  useEffect(() => {
    const fetchPolygons = async () => {
      if (!map.current) return;
      
      try {
        const token = localStorage.getItem('token');
        const apiUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
        const res = await fetch(
          `${apiUrl}/api/data/polygons/grouped?day=${selectedDay}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!res.ok) {
          throw new Error('Failed to fetch polygons');
        }

        const data: PolygonGroup[] = await res.json();

        // Remove existing polygon layers
        const existingLayers =
          map.current
            ?.getStyle()
            .layers?.map((l) => l.id)
            .filter((id) => id.startsWith('polygon-')) || [];

        existingLayers.forEach((id) => {
          if (map.current?.getLayer(id)) {
            map.current.removeLayer(id);
          }
        });

        // Remove existing sources
        existingLayers.forEach((id) => {
          const sourceId = id.replace(/-label|-border$/, '');
          if (map.current?.getSource(sourceId)) {
            try {
              map.current.removeSource(sourceId);
            } catch (e) {
              console.warn(`Could not remove source ${sourceId}:`, e);
            }
          }
        });

        // Add new polygon layers
        data.forEach((group, groupIndex) => {
          group.polygons.forEach((poly, i) => {
            const id = `polygon-${groupIndex}-${i}`;
            if (!poly.coordinates || poly.coordinates.length === 0) {
              console.warn(`Skipping polygon ${id} - no coordinates`);
              return;
            }

            const color = `hsl(${(groupIndex * 36) % 360}, 70%, 60%)`;

            map.current?.addSource(id, {
              type: 'geojson',
              data: {
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [poly.coordinates],
                },
                properties: {
                  label: group.workAreaNumber.replace(/^0+/, ''),
                  color,
                },
              },
            });

            // Fill layer
            map.current?.addLayer({
              id,
              type: 'fill',
              source: id,
              paint: {
                'fill-color': ['get', 'color'],
                'fill-opacity': 0.5,
              },
            });

            // Border layer
            map.current?.addLayer({
              id: `${id}-border`,
              type: 'line',
              source: id,
              paint: {
                'line-color': ['get', 'color'],
                'line-width': 1.5,
              },
            });

            // Label layer
            map.current?.addLayer({
              id: `${id}-label`,
              type: 'symbol',
              source: id,
              layout: {
                'text-field': ['get', 'label'],
                'text-size': 12,
                'text-anchor': 'center',
              },
              paint: {
                'text-color': '#000',
              },
            });
          });
        });
        
      } catch (err) {
        console.error('Error fetching polygons:', err);
      }
    };

    if (!map.current) return;

    if (map.current.isStyleLoaded()) {
      fetchPolygons();
    } else {
      map.current.once('load', fetchPolygons);
    }
  }, [selectedDay]);

  const days: { value: DayOfWeek; label: string }[] = [
    { value: 'monday', label: 'Mon' },
    { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' },
    { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' },
    { value: 'saturday', label: 'Sat' },
    { value: 'sunday', label: 'Sun' },
  ];

  return (
    <>
      <DashboardPanel />

      <PageBody fluid fill>
        <PageHeader
          title="Coverage"
          subtitle="Which work areas are served on which day."
          actions={
            mapError ? undefined : (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={selectedDay}
              onChange={(_, value) => value && setSelectedDay(value as DayOfWeek)}
            >
              {days.map((day) => (
                <ToggleButton key={day.value} value={day.value} sx={{ px: 1.5 }}>
                  {day.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            )
          }
        />

        <Card sx={{ flex: 1, minHeight: 360, overflow: 'hidden', position: 'relative' }}>
          <Box ref={mapContainer} sx={{ position: 'absolute', inset: 0 }} />
          {mapError && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', p: 3 }}>
              <EmptyState icon={<MapIcon />} title="The map is unavailable" description={mapError} />
            </Box>
          )}
        </Card>
      </PageBody>
    </>
  );
};

export default CoverageMap;
